import { getBackendUrl } from "@Utils";
import { t } from "@i18n";
import axiosWrapper from "../../../../services/axiosWrapper";
export { setWorkflowResourceType } from "../../../../reducers/Workflows";

// Import shared types from server package - SINGLE SOURCE OF TRUTH
// These types are inferred from Zod schemas in the server workflows schema
import type {
  SaveTransitionRequest,
  SaveTransitionResponse,
  GetTransitionRequest,
  GetTransitionResponse,
  GetStatusesInTransitionsRequest,
  GetStatusesInTransitionsResponse,
  ReviewLevelUIState,
  TransitionSelectionState,
  TransitionResult,
  // Save Workflow types - discriminated union for strict typing
  CreateWorkflowRequest,
  UpdateWorkflowRequest,
  SaveWorkflowRequest,
  SaveWorkflowResponse,
  WorkflowResourceType,
  // Add Reviewer to Level types - strict typing for reviewer management
  AddReviewerToLevelRequest,
  AddReviewerToLevelResponse,
  // Remove Reviewer from Level types
  RemoveReviewerFromLevelRequest,
  RemoveReviewerFromLevelResponse,
  // Duplicate Workflow types
  DuplicateWorkflowRequest,
  DuplicateWorkflowResponse,
  // Delete Workflows types
  DeleteWorkflowsRequest,
  DeleteWorkflowsResponse,
  // Additional response types
  SaveReviewLevelResponse,
  WorkflowReviewLevelsServiceResponse,
  GetWorkflowReviewLevelDataResponse,
  AddInitiatorToWorkflowResponse,
  RemoveInitiatorFromWorkflowResponse,
  ChangeArchivedWorkFlowStatusResponse,
  AddReviewFlowsToLibraryResponse,
  TransitionsServiceResponse,
  GetStatusListInWorkflowResponse,
  SaveWorkflowStatusListResponse,
} from "@ServerTypes/workflows/workflows.schema.js";
import type { ReviewsServiceResponse, GetChecklistsResponse } from "@ServerTypes/reviews/reviews.schema.js";
import type { GetPortalLevelStatusListResponse } from "@ServerTypes/portal/portal.schema.js";

// Re-export types for use in components
export type {
  SaveTransitionRequest,
  SaveTransitionResponse,
  GetTransitionRequest,
  GetTransitionResponse,
  GetStatusesInTransitionsRequest,
  GetStatusesInTransitionsResponse,
  ReviewLevelUIState,
  TransitionSelectionState,
  TransitionResult,
  // Save Workflow types
  CreateWorkflowRequest,
  UpdateWorkflowRequest,
  SaveWorkflowRequest,
  SaveWorkflowResponse,
  WorkflowResourceType,
  // Add Reviewer to Level types
  AddReviewerToLevelRequest,
  AddReviewerToLevelResponse,
  // Remove Reviewer from Level types
  RemoveReviewerFromLevelRequest,
  RemoveReviewerFromLevelResponse,
  // Duplicate Workflow types
  DuplicateWorkflowRequest,
  DuplicateWorkflowResponse,
  // Delete Workflows types
  DeleteWorkflowsRequest,
  DeleteWorkflowsResponse,
};

export interface ApiResponse<T = any> {
  data: T;
  status: number;
  [key: string]: any;
}

export interface TemplateArgs {
  createdByTemplate?: any;
  createdDateTemplate?: any;
  statusFromTemplate?: any;
  statusToTemplate?: any;
  reviewLevelsTemplate?: any;
  reviewLevelTemplate?: any;
  reviewersTemplate?: any;
  reviewLevelFilterTemplate?: any;
}

export interface ToolbarTemplateArgs {
  [key: string]: any;
}

/**
 * Save (create or update) a workflow.
 * Uses discriminated union type for strict compile-time validation.
 *
 * @param data - CreateWorkflowRequest (without _id) or UpdateWorkflowRequest (with _id)
 * @returns Typed response with status and optional error message
 *
 * @example
 * // Create new workflow
 * await saveWorkflow({ name: "My Workflow", spaceId: "...", description: "..." });
 *
 * @example
 * // Update existing workflow
 * await saveWorkflow({ _id: "...", name: "Updated Name", checklistIds: ["..."] });
 */
export const saveWorkflow = async (data: SaveWorkflowRequest): Promise<ApiResponse<SaveWorkflowResponse>> =>
  await axiosWrapper.post<SaveWorkflowResponse>(`${getBackendUrl()}/workflows/saveWorkflow`, data);

/**
 * Delete one or more workflows by their IDs.
 * @param data - Typed request with ids array and optional projectId
 * @returns Typed response with status
 */
export const deleteWorkflows = async (data: DeleteWorkflowsRequest): Promise<ApiResponse<DeleteWorkflowsResponse>> =>
  await axiosWrapper.post<DeleteWorkflowsResponse>(`${getBackendUrl()}/workflows/deleteWorkflows`, data);

export const saveReviewLevel = async (data: any): Promise<ApiResponse> =>
  await axiosWrapper.post<SaveReviewLevelResponse>(`${getBackendUrl()}/workflows/saveReviewLevel`, data);

export const fetchWorkflowLevels = async (data: any): Promise<ApiResponse> =>
  await axiosWrapper.post<WorkflowReviewLevelsServiceResponse>(
    `${getBackendUrl()}/workflows/workflowReviewLevelsService`,
    data,
  );

export const fetchreviewsUrl = async (data: any): Promise<ApiResponse> =>
  await axiosWrapper.post<ReviewsServiceResponse>(`${getBackendUrl()}/reviews/reviewsService`, data);

export const fetchchecklistData = async (data: any): Promise<ApiResponse> =>
  await axiosWrapper.post<GetChecklistsResponse>(`${getBackendUrl()}/reviews/getChecklists`, data);

export const getReviewLevelData = async (data: any): Promise<ApiResponse> =>
  await axiosWrapper.post<GetWorkflowReviewLevelDataResponse>(
    `${getBackendUrl()}/workflows/getWorkflowReviewLevelData`,
    data,
  );

/**
 * Add reviewer(s) to a workflow review level.
 * @param data - Typed request with levelId, workflowId, reviewerIds, and optional transitionUsers
 * @returns Typed response with status and optional error message
 */
export const addReviewerToLevel = async (
  data: AddReviewerToLevelRequest,
): Promise<ApiResponse<AddReviewerToLevelResponse>> =>
  await axiosWrapper.post<AddReviewerToLevelResponse>(`${getBackendUrl()}/workflows/addReviewerToLevel`, data);

export const addInitiatorToWorkflow = async (data: any): Promise<ApiResponse> =>
  await axiosWrapper.post<AddInitiatorToWorkflowResponse>(`${getBackendUrl()}/workflows/addInitiatorToWorkflow`, data);

/**
 * Remove a reviewer from a workflow review level.
 * @param data - Typed request with levelId, workflowId, and reviewerId
 * @returns Typed response with status and optional error message
 */
export const removeReviewerFromLevel = async (
  data: RemoveReviewerFromLevelRequest,
): Promise<ApiResponse<RemoveReviewerFromLevelResponse>> =>
  await axiosWrapper.post<RemoveReviewerFromLevelResponse>(
    `${getBackendUrl()}/workflows/removeReviewerFromLevel`,
    data,
  );

export const removeInitiatorFromWorkflow = async (data: any): Promise<ApiResponse> =>
  await axiosWrapper.post<RemoveInitiatorFromWorkflowResponse>(
    `${getBackendUrl()}/workflows/removeInitiatorFromWorkflow`,
    data,
  );

/**
 * Duplicate one or more workflows to a space.
 * @param data - Typed request with workflowIds, spaceId, and optional flags
 * @returns Typed response with status
 */
export const duplicateWorkflow = async (
  data: DuplicateWorkflowRequest,
): Promise<ApiResponse<DuplicateWorkflowResponse>> =>
  await axiosWrapper.post<DuplicateWorkflowResponse>(`${getBackendUrl()}/workflows/duplicateWorkflow`, data);

export const changeArchivedWorkflowStatus = async (data: any): Promise<ApiResponse> =>
  await axiosWrapper.post<ChangeArchivedWorkFlowStatusResponse>(
    `${getBackendUrl()}/workflows/changeArchivedWorkflowStatus`,
    data,
  );

export const addReviewFlowsToLibrary = async (data: any): Promise<ApiResponse> =>
  await axiosWrapper.post<AddReviewFlowsToLibraryResponse>(
    `${getBackendUrl()}/workflows/addReviewFlowsToLibrary`,
    data,
  );

/**
 * Save (create or update) a workflow transition.
 * @param data - Typed request payload validated against shared Zod schema
 * @returns Typed response with status and optional error message
 */
export const saveTransition = async (data: SaveTransitionRequest): Promise<ApiResponse<SaveTransitionResponse>> =>
  await axiosWrapper.post<SaveTransitionResponse>(`${getBackendUrl()}/workflows/saveTransition`, data);

export const getPortalLevelStatusList = async (data: any): Promise<ApiResponse> =>
  await axiosWrapper.post<GetPortalLevelStatusListResponse>(`${getBackendUrl()}/portal/getPortalLevelStatusList`, data);

/**
 * Get a specific transition by ID.
 * @param data - Typed request with transitionId and workflowId
 * @returns Typed response with transition result
 */
export const getTransition = async (data: GetTransitionRequest): Promise<ApiResponse<GetTransitionResponse>> =>
  await axiosWrapper.post<GetTransitionResponse>(`${getBackendUrl()}/workflows/getTransition`, data);

export const getTransitionsList = async (data: any): Promise<ApiResponse> =>
  await axiosWrapper.post<TransitionsServiceResponse>(`${getBackendUrl()}/workflows/transitionsService`, data);

export const getStatusListInWorkflow = async (data: any): Promise<ApiResponse> =>
  await axiosWrapper.post<GetStatusListInWorkflowResponse>(
    `${getBackendUrl()}/workflows/getStatusListInWorkflow`,
    data,
  );

/**
 * Get statuses used in transitions for a workflow.
 * @param data - Typed request with workflowId and resourceType
 * @returns Typed response with status list
 */
export const getStatusListInTransitions = async (
  data: GetStatusesInTransitionsRequest,
): Promise<ApiResponse<GetStatusesInTransitionsResponse>> =>
  await axiosWrapper.post<GetStatusesInTransitionsResponse>(
    `${getBackendUrl()}/workflows/getStatusesInTransitions`,
    data,
  );

export const saveWorkflowStatusList = async (data: any): Promise<ApiResponse> =>
  await axiosWrapper.post<SaveWorkflowStatusListResponse>(`${getBackendUrl()}/workflows/saveWorkflowStatusList`, data);

// ---------- GRID COLUMNS ----------

export const workflowGridColumns = ({ createdByTemplate, createdDateTemplate }: TemplateArgs) => [
  {
    type: "checkbox",
    width: "30",
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "_id",
    headerText: "",
    width: "0",
    visible: false,
    showInColumnChooser: false,
    isPrimaryKey: true,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "name",
    headerText: t("Name"),
    allowFiltering: true,
    allowEditing: false,
    allowSorting: true,
  },
  {
    field: "description",
    headerText: t("Description"),
    allowFiltering: true,
    allowEditing: false,
    allowSorting: true,
  },
  {
    field: "createdBy",
    headerText: t("Created by"),
    type: "string",
    template: createdByTemplate,
    editTemplate: createdByTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: true,
  },
  {
    field: "createdDate",
    headerText: t("Created On"),
    template: createdDateTemplate,
    editTemplate: createdDateTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: true,
  },
];

// ---------- TOOLBARS ----------

export const getWorkflowToolbar = ({
  editBtnTemplate,
  deleteBtnTemplate,
  duplicateBtnTemplate,
  addToLibraryBtnTemplate,
  craeteReviewFlowCopyBtnTemplate,
  archiveBtnTemplate,
  viewArchiveAndUnarchiveWorkflowTemplate,
  isArchived,
}: ToolbarTemplateArgs & { isArchived: boolean }) => {
  const toolbar: any[] = [];

  if (!isArchived) {
    toolbar.push(
      { id: "create_review_flow_copy_dropdown", align: "Left" as const, template: craeteReviewFlowCopyBtnTemplate },
      { id: "edit_workflow", align: "Left" as const, template: editBtnTemplate },
      { id: "delete_workflow", align: "Left" as const, template: deleteBtnTemplate },
      { id: "duplicate_workflow", align: "Left" as const, template: duplicateBtnTemplate },
      { id: "archive_workflow", align: "Left" as const, template: archiveBtnTemplate },
      { id: "add_to_library", align: "Left" as const, template: addToLibraryBtnTemplate },
    );
  } else {
    toolbar.push(
      { id: "delete_workflow", align: "Left" as const, template: deleteBtnTemplate },
      { id: "archive_workflow", align: "Left" as const, template: archiveBtnTemplate },
    );
  }

  toolbar.push(
    {
      id: "view_Archive_And_Unarchive_Workflow",
      align: "Right" as const,
      template: viewArchiveAndUnarchiveWorkflowTemplate,
    },
    "ColumnChooser",
  );

  return toolbar;
};

export const getTransitionsToolbar = ({
  createBtnTemplate,
  editBtnTemplate,
  deleteBtnTemplate,
}: ToolbarTemplateArgs) => [
  { id: "create_transition", align: "Left" as const, template: createBtnTemplate },
  { id: "edit_transition", align: "Left" as const, template: editBtnTemplate },
  { id: "delete_transition", align: "Left" as const, template: deleteBtnTemplate },
  "ColumnChooser",
];

export const transitionsGridColumns = ({
  statusFromTemplate,
  statusToTemplate,
  reviewLevelsTemplate,
}: TemplateArgs) => [
  {
    type: "checkbox",
    width: "40",
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    width: 0,
    field: "_id",
    headerText: "",
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
    showInColumnChooser: false,
    isPrimaryKey: true,
    visible: false,
  },
  {
    field: "name",
    headerText: t("Transition Name"),
    allowFiltering: true,
    allowEditing: false,
    allowSorting: true,
  },
  {
    field: "description",
    headerText: t("Description"),
    allowFiltering: true,
    allowEditing: false,
    allowSorting: true,
  },
  {
    field: "statusFrom",
    headerText: t("From Status"),
    template: statusFromTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "statusTo",
    headerText: t("To Status"),
    template: statusToTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    field: "levels",
    headerText: t("Levels"),
    width: "350",
    template: reviewLevelsTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
];

export const getReviewLevelsGridColumns = ({
  reviewLevelTemplate,
  reviewersTemplate,
  reviewLevelFilterTemplate,
}: TemplateArgs) => [
  {
    type: "checkbox",
    width: "25",
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
  {
    width: 0,
    field: "_id",
    headerText: "",
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
    showInColumnChooser: false,
    isPrimaryKey: true,
    visible: false,
  },
  {
    field: "serial",
    headerText: t("Review Levels"),
    filter: { type: "Menu" },
    width: "50",
    type: "number",
    template: reviewLevelTemplate,
    filterTemplate: reviewLevelFilterTemplate,
    allowFiltering: true,
    allowEditing: false,
    allowSorting: true,
  },
  {
    field: "title",
    width: "80",
    headerText: t("Level Title"),
    allowFiltering: true,
    allowEditing: false,
    allowSorting: true,
  },
  {
    field: "reviewers",
    headerText: t("Reviewers"),
    template: reviewersTemplate,
    allowFiltering: false,
    allowEditing: false,
    allowSorting: false,
  },
];

export const getWorkflowReviewLevelsToolbar = ({
  createBtnTemplate,
  editBtnTemplate,
  deleteBtnTemplate,
}: ToolbarTemplateArgs) => [
  { id: "create_review_level", template: createBtnTemplate, align: "Left" as const },
  { id: "edit_review_level", template: editBtnTemplate, align: "Left" as const },
  { id: "delete_review_level", template: deleteBtnTemplate, align: "Left" as const },
  "ColumnChooser",
];

// ---------- LEVEL STATE HELPERS ----------
// Using shared types from helpers package for consistency

// Local type aliases for backward compatibility and convenience
type ReviewLevel = ReviewLevelUIState;

/**
 * Handle checkbox change for an entire review level.
 * Toggles all reviewers in the level based on current state.
 */
export const levelCheckboxChange = (
  reviewLevels: ReviewLevel[],
  targetLevelId: string,
): { levelsUpdated: ReviewLevel[]; areReviewLevelsValid: boolean } => {
  let areReviewLevelsValid = false;

  const levelsUpdated = reviewLevels.map((level) => {
    const { _id, levelSelectionState, reviewers } = level;

    if (_id !== targetLevelId) {
      if (levelSelectionState === "partial" || levelSelectionState === "complete") {
        areReviewLevelsValid = true;
      }
      return level;
    }

    if (levelSelectionState === "empty" || levelSelectionState === "partial") {
      const updatedReviewers = reviewers.map((r) => ({ ...r, isReviewerSelected: true }));
      areReviewLevelsValid = true;
      return { ...level, levelSelectionState: "complete" as TransitionSelectionState, reviewers: updatedReviewers };
    }

    if (levelSelectionState === "complete") {
      const updatedReviewers = reviewers.map((r) => ({ ...r, isReviewerSelected: false }));
      return { ...level, levelSelectionState: "empty" as TransitionSelectionState, reviewers: updatedReviewers };
    }

    return level;
  });

  return { levelsUpdated, areReviewLevelsValid };
};

/**
 * Calculate the selection state based on how many reviewers are selected.
 */
const calculateLevelSelectionState = (selectedCount: number, totalCount: number): TransitionSelectionState => {
  if (selectedCount === 0) return "empty";
  if (selectedCount < totalCount) return "partial";
  return "complete";
};

/**
 * Handle checkbox change for an individual reviewer.
 * Updates the level's selection state based on the new reviewer selections.
 */
export const reviewerCheckboxChange = (
  reviewLevels: ReviewLevel[],
  targetLevelId: string,
  userId: string,
): { levelsUpdated: ReviewLevel[]; areReviewLevelsValid: boolean } => {
  let areReviewLevelsValid = false;

  const levelsUpdated = reviewLevels.map((level) => {
    const { _id, reviewers, levelSelectionState } = level;

    if (_id !== targetLevelId) {
      if (levelSelectionState === "partial" || levelSelectionState === "complete") {
        areReviewLevelsValid = true;
      }
      return level;
    }

    const updatedReviewers = reviewers.map((r) =>
      r.userId === userId ? { ...r, isReviewerSelected: !r.isReviewerSelected } : r,
    );

    const selectedCount = updatedReviewers.filter((r) => r.isReviewerSelected).length;
    const updatedState = calculateLevelSelectionState(selectedCount, updatedReviewers.length);

    if (updatedState === "partial" || updatedState === "complete") {
      areReviewLevelsValid = true;
    }

    return { ...level, levelSelectionState: updatedState, reviewers: updatedReviewers };
  });

  return { levelsUpdated, areReviewLevelsValid };
};
